const inFlightByUser = new Map<string, number>();
const readyUsers = new Set<string>();
const pendingUsers = new Set<string>();

function getInFlightCount(userId: string): number {
    return inFlightByUser.get(userId) ?? 0;
}

export function markBootstrapPendingForUser(userId: string | null | undefined): void {
    if (!userId) return;
    console.log(`[syncLifecycle] markBootstrapPendingForUser: ${userId}`);
    pendingUsers.add(userId);
    readyUsers.delete(userId);
}

export function beginBootstrapSyncForUser(userId: string | null | undefined): void {
    if (!userId) return;
    pendingUsers.add(userId);
    readyUsers.delete(userId);
    const count = getInFlightCount(userId) + 1;
    console.log(`[syncLifecycle] beginBootstrapSyncForUser: ${userId}, new count: ${count}`);
    inFlightByUser.set(userId, count);
}

export function endBootstrapSyncForUser(
    userId: string | null | undefined,
    options: { succeeded: boolean }
): void {
    if (!userId) return;

    const nextCount = Math.max(0, getInFlightCount(userId) - 1);
    console.log(`[syncLifecycle] endBootstrapSyncForUser: ${userId}, succeeded: ${options.succeeded}, nextCount: ${nextCount}`);

    if (nextCount === 0) {
        inFlightByUser.delete(userId);
        pendingUsers.delete(userId);
        if (options.succeeded) {
            console.log(`[syncLifecycle] Marking user ${userId} as READY.`);
            readyUsers.add(userId);
        } else {
            console.log(`[syncLifecycle] Marking user ${userId} as NOT READY (failed).`);
            readyUsers.delete(userId);
        }
        return;
    }

    inFlightByUser.set(userId, nextCount);
    if (!options.succeeded) {
        console.log(`[syncLifecycle] Marking user ${userId} as NOT READY (partial failure).`);
        readyUsers.delete(userId);
    }
}

export function markBootstrapReadyForUser(userId: string | null | undefined): void {
    if (!userId) return;
    console.log(`[syncLifecycle] markBootstrapReadyForUser called for: ${userId}`);
    if (getInFlightCount(userId) > 0) {
        console.log(`[syncLifecycle]   ...ignored, inflight count is > 0`);
        return;
    }
    console.log(`[syncLifecycle]   ...user is now READY.`);
    pendingUsers.delete(userId);
    readyUsers.add(userId);
}

export function canWriteCloudForUser(userId: string | null | undefined): boolean {
    if (!userId) return false;
    const inflight = getInFlightCount(userId);
    const hasReady = readyUsers.has(userId);

    // [AE-SYNC-RESILIENCE]
    // If we have no active syncs in flight, we allow writes even if the bootstrap
    // wasn't marked as fully "ready". This prevents a permanent "Bootstrap Lock" 
    // where a user cannot save settings if their initial sync had a minor error 
    // or timed out.
    const can = inflight === 0;

    if (inflight > 0) {
        console.warn(`[syncLifecycle] canWriteCloudForUser(${userId}) = false. Currently inflight (${inflight}).`);
    } else if (!hasReady) {
        console.info(`[syncLifecycle] canWriteCloudForUser(${userId}) = true (resilient). Not in readyUsers set, but no active syncs.`);
    }

    return can;
}

export function isBootstrapPendingForUser(userId: string | null | undefined): boolean {
    if (!userId) return false;
    return pendingUsers.has(userId) || getInFlightCount(userId) > 0;
}

export function resetSyncLifecycleForUser(userId: string | null | undefined): void {
    if (!userId) return;
    console.log(`[syncLifecycle] resetSyncLifecycleForUser: ${userId}`);
    inFlightByUser.delete(userId);
    pendingUsers.delete(userId);
    readyUsers.delete(userId);
}

export function resetSyncLifecycleState(): void {
    console.log(`[syncLifecycle] resetSyncLifecycleState`);
    inFlightByUser.clear();
    pendingUsers.clear();
    readyUsers.clear();
}
